/**
 * backendHealth — a tiny global store for backend connectivity state.
 *
 * The axios response interceptor in `client.ts` reports outcomes here:
 *   - a network failure or 5xx response flips state to `error`
 *   - any successful response clears it back to `ok`
 *
 * 401s and ordinary 4xx are NOT connectivity errors — they're auth/app
 * concerns each view already surfaces — so they don't touch this store.
 *
 * `<BackendErrorBanner />` subscribes via useSyncExternalStore and renders
 * a global top strip only while state is `error`. This replaces the old
 * per-page LiveBackendStatus label: connectivity problems aren't
 * homepage-specific, so the indicator lives app-wide and stays invisible
 * unless something is actually wrong.
 */
type Health = { status: 'ok' | 'error'; message: string };

let state: Health = { status: 'ok', message: '' };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function reportBackendError(message: string) {
  if (state.status === 'error' && state.message === message) return;
  state = { status: 'error', message };
  emit();
}

export function reportBackendOk() {
  if (state.status === 'ok') return;
  state = { status: 'ok', message: '' };
  emit();
}

export function subscribeBackendHealth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getBackendHealth(): Health {
  return state;
}
